import { useCallback, useMemo } from 'react';

import { useAppSelector } from '@app/store';
import {
  accessRightsInitialState,
  adminAccessRights,
} from '@app/store/slices/AccessRights/constants';
import type {
  AccessRightsModuleKeys,
  UserAccessRightsInterface,
} from '@app/types/NewAccessRight';

type OrganizationAccessRight = {
  Name: string;
  RightType: number;
  Allowed: boolean;
};

const normalizeName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^0-9a-zа-я]+/giu, '');

const ORG_RIGHTTYPE_TO_MODULE_KEY: Record<number, AccessRightsModuleKeys> = {
  3: 'CrmTasks',
  4: 'Records',
  5: 'SalesFunnels',
  6: 'Integrations',
  7: 'Sendouts',
  8: 'AiOperator',
  9: 'Warehouse',
};

const ORG_NAME_TO_MODULE_KEY: Record<string, AccessRightsModuleKeys> = {
  [normalizeName('Задачи')]: 'CrmTasks',
  [normalizeName('Записи')]: 'Records',
  [normalizeName('Воронки продаж')]: 'SalesFunnels',
  [normalizeName('Whats App интеграции')]: 'Integrations',
  [normalizeName('WhatsApp интеграции')]: 'Integrations',
  [normalizeName('Рассылки')]: 'Sendouts',
  [normalizeName('AI оператор')]: 'AiOperator',
  [normalizeName('Склад')]: 'Warehouse',
};

const ORG_CONTROLLED_MODULE_KEYS: AccessRightsModuleKeys[] = [
  'CrmTasks',
  'Records',
  'SalesFunnels',
  'Integrations',
  'Sendouts',
  'AiOperator',
  'Warehouse',
];

const disableBooleanFlags = <T extends Record<string, unknown>>(
  module: T
): T => {
  const nextEntries = Object.entries(module).map(([key, value]) => {
    if (typeof value === 'boolean') return [key, false];
    return [key, value];
  });

  return Object.fromEntries(nextEntries) as T;
};

export const useNewUserAccessRights = () => {
  const newAccessRights = useAppSelector(
    ({ newAccessRights }) => newAccessRights
  );

  const accessRightsFromLocalStorage: typeof newAccessRights | null =
    useMemo(() => {
      if (typeof window === 'undefined') return null;

      try {
        const raw = window.localStorage.getItem('NewUserAccessRights');
        if (!raw) return null;
        return JSON.parse(raw) as typeof newAccessRights;
      } catch {
        return null;
      }
    }, []);

  const { user, organization } = Boolean(newAccessRights?.user?.UserId)
    ? newAccessRights
    : accessRightsFromLocalStorage || {};

  const organizationAccessRights = useMemo<OrganizationAccessRight[]>(() => {
    return Array.isArray(organization)
      ? (organization as OrganizationAccessRight[])
      : [];
  }, [organization]);

  const baseUserAccessRights: UserAccessRightsInterface = useMemo(() => {
    const isAdmin = Boolean(user?.IsAdmin || user?.IsSuperAdmin);

    if (isAdmin) {
      return {
        ...adminAccessRights,
        UserId: user?.UserId ?? 0,
        IsSuperAdmin: user?.IsSuperAdmin ?? false,
        IsAdmin: user?.IsAdmin ?? false,
      };
    }

    return user ?? accessRightsInitialState.user;
  }, [user]);

  const orgAllowedByModuleKey = useMemo(() => {
    const map = new Map<AccessRightsModuleKeys, boolean>();

    for (const right of organizationAccessRights) {
      const moduleKey =
        ORG_RIGHTTYPE_TO_MODULE_KEY[right.RightType] ??
        ORG_NAME_TO_MODULE_KEY[normalizeName(right.Name)];

      if (moduleKey) {
        map.set(moduleKey, Boolean(right.Allowed));
      }
    }

    return map;
  }, [organizationAccessRights]);

  const getModuleAccess = useCallback(
    (moduleKey: AccessRightsModuleKeys) =>
      orgAllowedByModuleKey.get(moduleKey) ?? true,
    [orgAllowedByModuleKey]
  );

  const userAccessRights: UserAccessRightsInterface = useMemo(() => {
    let next: UserAccessRightsInterface = baseUserAccessRights;

    for (const moduleKey of ORG_CONTROLLED_MODULE_KEYS) {
      if (getModuleAccess(moduleKey)) continue;

      if (next === baseUserAccessRights) {
        next = { ...baseUserAccessRights };
      }

      const currentModule = next[moduleKey] as unknown as Record<
        string,
        unknown
      >;
      next[moduleKey] = disableBooleanFlags(currentModule) as never;
    }

    if (Array.isArray(next.Modules) && next.Modules.length > 0) {
      const normalizedModules = next.Modules.map((moduleRight) => {
        const moduleKey =
          ORG_RIGHTTYPE_TO_MODULE_KEY[moduleRight.RightType] ??
          ORG_NAME_TO_MODULE_KEY[normalizeName(moduleRight.Name)];

        if (!moduleKey) return moduleRight;

        return getModuleAccess(moduleKey)
          ? moduleRight
          : { ...moduleRight, Allowed: false };
      });

      if (normalizedModules !== next.Modules) {
        next = { ...next, Modules: normalizedModules };
      }
    }

    return next;
  }, [baseUserAccessRights, getModuleAccess]);

  return {
    userAccessRights,
    organizationAccessRights,
  };
};
